import { downloadCsv } from '@/utils/csv';
import type { ImportError } from './types';

export type SkippedImportRow = {
  row: number;
  reason: string;
};

/** Excel-style row numbers (header = 1, first data row = 2). */
export function errorRowNumbers(errors: ImportError[] | undefined | null): number[] {
  const rows = new Set<number>();
  for (const err of errors || []) {
    const n = Number(err.row);
    if (Number.isFinite(n) && n >= 2) rows.add(n);
  }
  return [...rows].sort((a, b) => a - b);
}

/**
 * Download original file columns for rows that failed validation/import,
 * plus Error Field / Error Message so staff can fix and re-upload only those.
 */
export function downloadErrorRowsCsv(opts: {
  filename: string;
  headers: string[];
  rawRows: Record<string, string>[];
  errors: ImportError[];
}) {
  const { filename, headers, rawRows, errors } = opts;
  const byRow = new Map<number, ImportError[]>();
  for (const err of errors) {
    const n = Number(err.row);
    if (!Number.isFinite(n) || n < 2) continue;
    const list = byRow.get(n) || [];
    list.push(err);
    byRow.set(n, list);
  }

  const sortedRows = [...byRow.keys()].sort((a, b) => a - b);
  if (!sortedRows.length) return false;

  const csvHeaders = [
    ...headers.map((h) => ({ key: h, label: h })),
    { key: '__error_field', label: 'Error Field' },
    { key: '__error_message', label: 'Error Message' },
    { key: '__source_row', label: 'Source Row' },
  ];

  const csvRows = sortedRows.map((rowNum) => {
    const idx = rowNum - 2;
    const raw = rawRows[idx] || {};
    const errs = byRow.get(rowNum) || [];
    const row: Record<string, unknown> = { __source_row: rowNum };
    for (const h of headers) {
      row[h] = raw[h] ?? '';
    }
    row.__error_field = errs.map((e) => e.field || '').filter(Boolean).join('; ');
    row.__error_message = errs.map((e) => e.message || '').filter(Boolean).join(' | ');
    return row;
  });

  downloadCsv(filename, csvHeaders, csvRows);
  return true;
}

/** Download original columns for client-skipped rows, with skip reason. */
export function downloadSkippedRowsCsv(opts: {
  filename: string;
  headers: string[];
  rawRows: Record<string, string>[];
  skipped: SkippedImportRow[];
}) {
  const { filename, headers, rawRows, skipped } = opts;
  const sorted = [...skipped]
    .filter((s) => Number.isFinite(s.row) && s.row >= 2)
    .sort((a, b) => a.row - b.row);
  if (!sorted.length) return false;

  const csvHeaders = [
    ...headers.map((h) => ({ key: h, label: h })),
    { key: '__skip_reason', label: 'Skip Reason' },
    { key: '__source_row', label: 'Source Row' },
  ];

  const csvRows = sorted.map((item) => {
    const raw = rawRows[item.row - 2] || {};
    const row: Record<string, unknown> = {
      __source_row: item.row,
      __skip_reason: item.reason || 'Skipped',
    };
    for (const h of headers) {
      row[h] = raw[h] ?? '';
    }
    return row;
  });

  downloadCsv(filename, csvHeaders, csvRows);
  return true;
}
